import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { ProjectTaskFragmentFragment, ProjectTaskStatus, TasksPermission } from 'gql/index';
import { useGetMe } from '../../../../utils/useGetMe';
import { useTaskMessages } from '@modules/tasks/taskUtils';

export type ProjectTasksOwnershipFilterKey = 'mine' | 'all';
export type ProjectTasksTimelineFilterKey = 'incomplete' | 'today' | 'upcoming' | 'completed';

export type UseProjectTasksFiltersParams = {
  tasks: ProjectTaskFragmentFragment[];
  tasksPermission?: TasksPermission | null;
};

export type UseProjectTasksFiltersResult = {
  search: string;
  setSearch: (value: string) => void;
  ownershipFilter: ProjectTasksOwnershipFilterKey;
  setOwnershipFilter: (value: ProjectTasksOwnershipFilterKey) => void;
  timelineFilter: ProjectTasksTimelineFilterKey;
  setTimelineFilter: (value: ProjectTasksTimelineFilterKey) => void;
  filteredTasks: ProjectTaskFragmentFragment[];
};

export function useProjectTasksFilters({ tasks, tasksPermission }: UseProjectTasksFiltersParams): UseProjectTasksFiltersResult {
  const { me } = useGetMe();
  const currentUserId = me?.id;
  const { getTaskName } = useTaskMessages();

  const [ownershipFilter, setOwnershipFilter] = useState<ProjectTasksOwnershipFilterKey>(
    tasksPermission === TasksPermission.LimitedAccess ? 'mine' : 'all'
  );
  const [timelineFilter, setTimelineFilter] = useState<ProjectTasksTimelineFilterKey>('incomplete');
  const [search, setSearch] = useState('');

  const filteredTasks = useMemo(() => {
    const now = dayjs();
    const endOfToday = dayjs().endOf('day');
    const term = search.toLowerCase();

    const applyOwnership = (task: ProjectTaskFragmentFragment) => {
      if (ownershipFilter !== 'mine' || !currentUserId) return true;
      return task.assignees?.some(a => a.id === currentUserId) ?? false;
    };

    const applyTimeline = (task: ProjectTaskFragmentFragment) => {
      if (timelineFilter === 'incomplete') {
        return task.status !== ProjectTaskStatus.Done;
      }
      if (timelineFilter === 'today') {
        return (
          task.status !== ProjectTaskStatus.Done &&
          ((task.dueDate != null && !dayjs(task.dueDate).isAfter(endOfToday)) ||
            (task.dueDate == null && task.startDate != null && !dayjs(task.startDate).isAfter(now)))
        );
      }
      if (timelineFilter === 'upcoming') {
        return (
          task.status !== ProjectTaskStatus.Done &&
          ((task.startDate != null && dayjs(task.startDate).isAfter(endOfToday)) ||
            (task.dueDate != null && dayjs(task.dueDate).isAfter(endOfToday)))
        );
      }
      if (timelineFilter === 'completed') {
        return task.status === ProjectTaskStatus.Done;
      }
      return true;
    };

    const applySearch = (task: ProjectTaskFragmentFragment) =>
      term === '' ||
      getTaskName(task).toLowerCase().includes(term) ||
      (task.project?.name ?? '').toLowerCase().includes(term) ||
      (task.document?.fileName ?? '').toLowerCase().includes(term) ||
      (task.projectForm?.formDefinition?.name ?? '').toLowerCase().includes(term);

    const sortByDueDate = (left: ProjectTaskFragmentFragment, right: ProjectTaskFragmentFragment) => {
      const leftDate = left.dueDate ?? left.startDate;
      const rightDate = right.dueDate ?? right.startDate;
      if (!leftDate && !rightDate) return 0;
      if (!leftDate) return 1;
      if (!rightDate) return -1;
      const leftValue = dayjs(leftDate).valueOf();
      const rightValue = dayjs(rightDate).valueOf();
      if (timelineFilter === 'completed') {
        return rightValue - leftValue;
      }
      return leftValue - rightValue;
    };

    const sortByCompletedOnDesc = (left: ProjectTaskFragmentFragment, right: ProjectTaskFragmentFragment) => {
      const leftCompletedOn = left.completedOn ? dayjs(left.completedOn).valueOf() : 0;
      const rightCompletedOn = right.completedOn ? dayjs(right.completedOn).valueOf() : 0;
      return rightCompletedOn - leftCompletedOn;
    };

    let result = [...tasks]
      .filter(applyOwnership)
      .filter(task => applyTimeline(task))
      .filter(task => applySearch(task));

    if (timelineFilter === 'completed') {
      result = result.sort(sortByCompletedOnDesc);
    } else {
      result = result.sort(sortByDueDate);
    }

    return result;
  }, [currentUserId, ownershipFilter, search, tasks, timelineFilter, getTaskName]);

  return {
    search,
    setSearch,
    ownershipFilter,
    setOwnershipFilter,
    timelineFilter,
    setTimelineFilter,
    filteredTasks
  };
}
